import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { PropTypes } from 'prop-types';
import { useNavigate } from "react-router-dom";
import { ad_single_site_TablePayload } from "../../../../../../../config/reqConfig/anomalyDetection";
import ApiService from "../../../../../../../config/api-config";
import { getColumns } from "./tableconfig";
import useColumns from "@harbor/elements/utils/table/hooks/columns";
import TableStore from "@harbor/elements/utils/table/store";
import ADTableView from "../../../genericComponents/ADTableView";
import { addUnitsFromDate } from "../../../../../../../utils/displayTime";
import css from "../../../common/anomalyMagneticStyle.less";
import CustomTimeStamp from "../../../../../../../generics/TimeStamp/customTimeStamp";
import AppHoc from "../../../../../../../generics/AppHOC";
import Spinner from "../../../../../../../common/Spinner";
import ErrorComponent from "../../../../../../../common/ErrorComponent";
import { downloadAsCSVFile } from "../../../../../../../utils/common";
import { payloadOnQueryUpdate } from "../../../common/adUtils";
import useI18n from "../../../../../../../i18n/useI18n";
import { AD_SITES_TABLE_DEFAULT_TGL, LOCAL_STORAGE_LABELS } from "../../../../../../../utils/enums";
import { useTableSettings } from "../../../../../../../utils/table";

let defaultSort = [['anomaly_rate_col', 'desc']];
const tableSettingsId = "ad_sites_table";

const ADTable = (props) => {
    const { tViews } = useI18n('views')
    const navigate = useNavigate();
    const tableStoreRef = useRef(null);
    const [tableStore, setTableStore] = useState(null);
    const [tableLoader, setTableLoader] = useState(true);
    const [tableError, setTableError] = useState('');
    const [tableLoaded, setTableLoaded] = useState(false);
    const payloadRef = React.useRef({});
    const [liveMessage, setLiveMessage] = useState("");

    const openTableSettings = useCallback(() => {
        tableStoreRef.current?.setState({ settingsOpen: true });
    }, []);

    let columns = useMemo(
        () => getColumns(navigate, props.setSelectedSite, openTableSettings),
        [navigate, props.setSelectedSite, openTableSettings]
    );

    const columnsHook = useColumns({
        config: () => columns,
        order: defaultSort
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch data logic
                let tableStore = new TableStore({
                    query: { pageLength : 30 },
                    hooks: [columnsHook],
                    fetchData: async ({ query, setSource, setCount }) => {
                        setTableLoader(true);
                        let payload = ad_single_site_TablePayload(props.selectedHour, props.selectType === "topSites");
                        payload = payloadOnQueryUpdate(query, payload);
                        let res = await ApiService.getADSitesTable(payload);
                        if (!res.errorObject && !query.export) {
                            setSource(res?.data?.data);
                            setCount(res?.data?.count, false);
                            tableStoreRef.current = tableStore;
                            setTableStore(tableStore)
                            setTableLoader(false);
                            setTableLoaded(true);
                        } else if (query.export) {
                            let resData = res?.data?.data;
                            const exportColumns = tableStore.getState().columns
                                .filter(item => item.prop !== "actions");
                            const data = [exportColumns.map(item => `"${item.name}"`).join(",")];
                            for (const item of resData) {
                                data.push(exportColumns.map(col => `"${item[col.prop] ?? ''}"`).join(","));
                            }
                            downloadAsCSVFile(data.join("\n"), tViews('anomalySummary'));
                            delete query.export;
                            setTableLoader(false);
                            setTableLoaded(true);
                        } else {
                            setTableError(res.errorObject);
                            setTableLoader(false);
                            setTableLoaded(true);
                        }
                    }
                });
                tableStoreRef.current = tableStore;
                useTableSettings(
                    LOCAL_STORAGE_LABELS.AD_SITES_TABLE,
                    AD_SITES_TABLE_DEFAULT_TGL,
                    columnsHook,
                    tableStore
                );
            }
            catch (error) {
                // Handle error
                setTableError(error.message);
                setTableLoader(false);
                setTableLoaded(true);
            }
        }
        fetchData();
    }, [props.selectedHour || props.selectType, props.globalFilter.onAnomalyRefresh]);

    useEffect(() => {
        if (tableLoader) {
            setLiveMessage("");
        } else if (!tableLoader && tableLoaded) {
            const timeout = setTimeout(() => {
                setLiveMessage(`${tViews('a11yLabels.loadingCompleted')}`);
            }, 100)
            return () => clearTimeout(timeout);
        }
    }, [tableLoader, tableLoaded]);

    return (
        <>
            <div 
            aria-live={tViews('a11yLabels.ariaLiveText')} 
            className={css["aria-live-style"]}
            >
                {liveMessage}
            </div>
            <div>
                {(tableStore
                    ? (<div className={css["anomaly-table"]}
                        data-cy="p-netw-anomaly-table"
                        data-testid="p-netw-anomaly-table">
                        <div className="customt-timestamp">
                            <CustomTimeStamp
                                prevTime={props.selectedHour}
                                currentTime={addUnitsFromDate(new Date(props.selectedHour), 1, "h")} />
                        </div>
                        <ADTableView
                            payload={payloadRef.current}
                            tableStore={tableStore}
                            header={tViews('a11yLabels.anomalySummaryHeader')}
                            status={{ isLoading: tableLoader, error: tableError }}
                            defaultSort={defaultSort}
                            columns={tableStore?.getState()?.columns}
                            id={"ad-sites-table"}
                            isPagination={true}
                            entity={tViews('optimization.sitesCount')}
                            tableSettings={{
                                localStorageName: LOCAL_STORAGE_LABELS.AD_SITES_TABLE,
                                defaultToggles: AD_SITES_TABLE_DEFAULT_TGL,
                                dataTestid: tableSettingsId
                            }} />
                    </div>)
                    : (
                        tableLoader
                            ? <Spinner />
                            : tableError &&
                            <ErrorComponent
                                code={"500"}
                                width={"110px"}
                                className={"large-dashlet-error"} />
                    )
                )}
            </div>
        </>
    );
}

ADTable.propTypes = {
    selectedHour: PropTypes.string,
    selectType: PropTypes.string,
    globalFilter: PropTypes.object,
    setSelectedSite: PropTypes.func.isRequired
}

export default AppHoc(ADTable);
